import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  TrendingUp as UpgradeIcon,
  OpenInNew as ExternalIcon,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { PrimaryButton } from '../common/PrimaryButton';
import { SecondaryButton } from '../common/SecondaryButton';

interface BillingData {
  plan: string;
  billingInterval: string | null;
  status: string;
  currentPeriodEnd: string | null;
  hasStripeCustomer: boolean;
  pendingUpgrade: boolean;
  autoUpgradedAt: string | null;
  autoUpgradedFrom: string | null;
  usage: {
    activeAccounts: number;
    eventsThisMonth: number;
  };
  limits: {
    accountLimit: number;
    eventLimit: number;
    eventRetentionDays: number;
  };
  availablePlans: Array<{
    id: string;
    accountLimit: number;
    eventLimit: number;
    eventRetentionDays: number;
    pricing: { monthly: number; annual: number };
  }>;
}

const PLAN_COLORS: Record<string, string> = {
  free: '#9e9e9e',
  starter: '#2196f3',
  growth: '#4caf50',
  pro: '#ff9800',
  enterprise: '#9c27b0',
};

export const BillingSettings: React.FC = () => {
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upgrading, setUpgrading] = useState<string | null>(null);

  useEffect(() => {
    loadBilling();
  }, []);

  const loadBilling = async () => {
    try {
      setLoading(true);
      const data = await api.getBilling();
      setBilling(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load billing info');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: string, interval: 'monthly' | 'annual') => {
    try {
      setUpgrading(plan);
      const { checkoutUrl } = await api.createCheckoutSession(plan, interval);
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start checkout');
      setUpgrading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const { portalUrl } = await api.createBillingPortal();
      window.open(portalUrl, '_blank');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to open billing portal');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!billing) {
    return <Alert severity="error">{error || 'Failed to load billing'}</Alert>;
  }

  const accountUsagePercent = Math.min(100, (billing.usage.activeAccounts / billing.limits.accountLimit) * 100);
  const eventUsagePercent = Math.min(100, (billing.usage.eventsThisMonth / billing.limits.eventLimit) * 100);

  const formatNumber = (n: number) => n.toLocaleString();

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {billing.pendingUpgrade && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your plan was auto-upgraded from {billing.autoUpgradedFrom} to {billing.plan} due to usage exceeding limits.
          Please complete the payment to keep your upgraded plan.
        </Alert>
      )}

      {/* Current Plan */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Current Plan
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={billing.plan.charAt(0).toUpperCase() + billing.plan.slice(1)}
                sx={{
                  bgcolor: PLAN_COLORS[billing.plan] || '#9e9e9e',
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
              {billing.billingInterval && (
                <Typography variant="body2" color="text.secondary">
                  ({billing.billingInterval})
                </Typography>
              )}
              <Chip
                label={billing.status}
                size="small"
                variant="outlined"
                color={billing.status === 'active' ? 'success' : billing.status === 'past_due' ? 'warning' : 'default'}
              />
            </Box>
          </Box>

          {billing.hasStripeCustomer && (
            <SecondaryButton
              startIcon={<CreditCardIcon />}
              endIcon={<ExternalIcon sx={{ fontSize: 16 }} />}
              onClick={handleManageBilling}
            >
              Manage Billing
            </SecondaryButton>
          )}
        </Box>

        {billing.currentPeriodEnd && (
          <Typography variant="body2" color="text.secondary">
            Next billing date: {new Date(billing.currentPeriodEnd).toLocaleDateString()}
          </Typography>
        )}
      </Paper>

      {/* Usage Meters */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Usage This Month
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 250px' }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" fontWeight={500}>Accounts</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatNumber(billing.usage.activeAccounts)} / {formatNumber(billing.limits.accountLimit)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={accountUsagePercent}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: accountUsagePercent > 90 ? '#f44336' : accountUsagePercent > 70 ? '#ff9800' : '#4caf50',
                  borderRadius: 4,
                },
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 250px' }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" fontWeight={500}>Events</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatNumber(billing.usage.eventsThisMonth)} / {formatNumber(billing.limits.eventLimit)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={eventUsagePercent}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: eventUsagePercent > 90 ? '#f44336' : eventUsagePercent > 70 ? '#ff9800' : '#4caf50',
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" mt={2}>
          Event retention: {billing.limits.eventRetentionDays} days
        </Typography>
      </Paper>

      {/* Available Plans */}
      {billing.plan !== 'enterprise' && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Upgrade Plan
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {billing.availablePlans
              .filter((p) => p.id !== 'free' && p.id !== 'enterprise' && p.id !== billing.plan)
              .filter((p) => {
                const planOrder = ['free', 'starter', 'growth', 'pro', 'enterprise'];
                return planOrder.indexOf(p.id) > planOrder.indexOf(billing.plan);
              })
              .map((plan) => (
                <Paper
                  key={plan.id}
                  variant="outlined"
                  sx={{ p: 2.5, textAlign: 'center', borderRadius: 2, flex: '1 1 200px', minWidth: 200 }}
                >
                  <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                    {plan.id}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} mt={1}>
                    ${plan.pricing.monthly}
                    <Typography component="span" variant="body2" color="text.secondary">
                      /mo
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    or ${plan.pricing.annual}/mo billed annually
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" mb={0.5}>
                    {formatNumber(plan.accountLimit)} accounts
                  </Typography>
                  <Typography variant="body2" mb={0.5}>
                    {formatNumber(plan.eventLimit)} events/mo
                  </Typography>
                  <Typography variant="body2" mb={2}>
                    {plan.eventRetentionDays} day retention
                  </Typography>
                  <Box display="flex" gap={1} justifyContent="center">
                    <PrimaryButton
                      size="small"
                      startIcon={<UpgradeIcon />}
                      onClick={() => handleUpgrade(plan.id, 'monthly')}
                      disabled={upgrading === plan.id}
                    >
                      {upgrading === plan.id ? <CircularProgress size={18} /> : 'Monthly'}
                    </PrimaryButton>
                    <SecondaryButton
                      size="small"
                      onClick={() => handleUpgrade(plan.id, 'annual')}
                      disabled={upgrading === plan.id}
                    >
                      Annual
                    </SecondaryButton>
                  </Box>
                </Paper>
              ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};
